import {
  ApiErrorResponse,
  InputDataDetailsType,
  InputDataInfoType,
  InputDataType,
  Spinner,
  useDescriptionInputDataInfoQuery,
  useDescriptionInputDataQuery,
  useInputDataDetailsQuery,
  useLoadViewState,
  useQueryParams,
  SampleCommentType,
  useDescriptionOutputSnapshotQuery,
  ISODate,
  parseSafeInteger,
  useDescriptionOutputSnapshotVersionQuery,
  getForbiddenErrorCode,
  useDataObjectGroupsWithDescriptionsQuery,
  DataObjectGroupType,
  keepStaleInMinutes,
  ISO_DATE_FORMAT,
  getDayjsNow,
  saveViewState,
  EditSessionContextProvider,
  type EditSessionContextProviderProps,
  useUpdateInputDataDraftsMutation,
  useInsertInputDataDraftMutation,
  useDeleteInputDataDraftsMutation,
  useCommitInputDataDraftsMutation,
  useDiscardInputDataEditSessionMutation,
  useInputDataEditSessionQuery,
  InputDataScopeKey,
  useEditSessionContext,
  OutputSnapshotVersionType,
  useMutateSubjectInputDataUpload,
  useAlertContext,
} from '@atlas-frontend/sdk';
import type { SortingState } from '@tanstack/react-table';
import { Forbidden } from '../../Forbidden';
import { NotFound } from '../../NotFound';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PaginationProps } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useHasPermissionToManageInputData } from '../useHasPermissionToManageInputData';
import translations from './InputDataSubjectDetail.translations.json';
import { useInputDataVersions } from './useInputDataVersions';
import { useInputDataDescriptions } from './useInputDataDescriptions';
import { TableFilterSortProps } from '../table/InputDataViewTable';
import {
  InputDataDetailsContextProvider,
  useInputDataDetailsStoreValue,
} from './InputDataDetailsContext';
import { InputDataDescriptionInfo, InputDataVersionInfo } from './types';
import { useSubmittedVersion } from './useSubmittedVersion';
import { OutputVersionSelectionModal } from './OutputVersionSelectionModal';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { useConformDateToValueDate } from '../../../common/useConformDateToValueDate';
import { keepPreviousData } from '@tanstack/react-query';
import { InputDataSubjectDetailsForm } from './form/InputDataSubjectDetailForm';
import { flushSync } from 'react-dom';
import { InputDataSubjectDetailView } from './view/InputDataSubjectDetailView';
import { useInputDataDirectory } from './useInputDataDirectory';
import { useInputDataPage } from './useInputDataPage';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-detail',
);
addTranslations(translations);

function acceptAll() {
  return Promise.resolve([]);
}

function setOutputParams(
  setSearchParams: SetURLSearchParams,
  output: OutputSnapshotVersionType,
) {
  const {
    outputVersion: { outputDovId: outputDataVersionId },
  } = output;
  setSearchParams((params) => {
    const newParams = new URLSearchParams(params);
    newParams.set('outputDataVersionId', outputDataVersionId.toString());
    newParams.delete('pageNumber');
    return newParams;
  });
}

const directoryStateStorageKey = 'inputDataSubjectDetails';

export function InputDataSubjectDetail() {
  const [
    {
      date,
      descriptionId,
      groupId,
      subject,
      periodSkipLastValueDate = false,
      outputDataVersionId,
    },
  ] = useQueryParams();

  const { data: treeData, isLoading: isLoadingTreeData } =
    useDataObjectGroupsWithDescriptionsQuery(
      { groupType: DataObjectGroupType.I },
      {
        placeholderData: keepPreviousData,
        ...keepStaleInMinutes(1),
      },
    );

  const conformedDate = useConformDateToValueDate({
    id: Number(descriptionId || groupId),
    date: date || getDayjsNow().format(ISO_DATE_FORMAT),
    periodSkipLastValueDate: periodSkipLastValueDate as boolean,
    treeData,
    dataObjectType: descriptionId ? 'description' : 'group',
  });

  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    directoryStateStorageKey,
    {
      sort: [],
    },
  );

  if (isLoadingTreeData) {
    return <Spinner />;
  }

  if (!isQueryParamCleared) {
    return null;
  }

  if (!(subject && conformedDate && (descriptionId || groupId))) {
    return <NotFound />;
  }

  return (
    <InputDataDetailsContextProvider>
      <InputDataSubjectDetailPage
        valueDate={conformedDate as ISODate}
        periodSkipLastValueDate={periodSkipLastValueDate as boolean}
        descriptionId={descriptionId}
        groupId={groupId}
        subject={subject}
        outputDataObjectVersionId={parseSafeInteger(outputDataVersionId)}
        initialFilters={viewState.filters}
        initialSort={viewState.sort}
        initialPagination={viewState.pagination}
      />
    </InputDataDetailsContextProvider>
  );
}

interface InputDataSubjectDetailPageProps {
  descriptionId?: string;
  groupId?: string;
  valueDate: ISODate;
  periodSkipLastValueDate?: boolean;
  subject: string;
  outputDataObjectVersionId?: number;
  output?: OutputSnapshotVersionType;
  initialFilters?: Partial<Record<string, any>>;
  initialSort?: SortingState;
  initialPagination?: PaginationProps;
}

interface SubjectDetails extends InputDataSubjectDetailPageProps {
  inputDataDetailsType?: InputDataDetailsType;
  versionData: InputDataVersionInfo;
  canManage: boolean;
  isLoading: boolean;
  onReload: () => Promise<any>;
  onAddComment: (comment: SampleCommentType) => void;
}

function InputDataSubjectDetailPage(props: InputDataSubjectDetailPageProps) {
  return <InputDataSubjectDetailsPage {...props} />;
}

function InputDataSubjectDetailsPage(props: InputDataSubjectDetailPageProps) {
  const { data, isFetching, refetch, setData } =
    useInputDataDetailsQuery(props);
  const versionData = useInputDataVersions(
    data?.descriptions,
    !!props.outputDataObjectVersionId,
  );
  const [mode] = useInputDataDetailsStoreValue('mode');
  const [showOutputs, setShowOutputs] =
    useInputDataDetailsStoreValue('showOutputs');
  const [, setSearchParams] = useSearchParams();
  const hasPermissionToManageInputData =
    useHasPermissionToManageInputData(
      Number(props.descriptionId || props.groupId),
      props.descriptionId ? 'description' : 'group',
    ) ?? false;
  const onUpdateComment = useCallback(
    (newComment: SampleCommentType) => {
      if (!data) return;
      const updatedData: InputDataDetailsType = {
        ...data,
        sampleComments: [...(data?.sampleComments || []), newComment],
      };
      setData(updatedData);
    },
    [data, setData],
  );

  const hideOutputSelect = useCallback(() => {
    setShowOutputs(false);
  }, [setShowOutputs]);

  const selectOutput = useCallback(
    (output: OutputSnapshotVersionType) => {
      setShowOutputs(false);
      versionData.setVersion('output');
      setOutputParams(setSearchParams, output);
    },
    [setSearchParams, setShowOutputs, versionData],
  );

  useEffect(() => {
    saveViewState('inputDataDetails', {
      descriptionId: props.descriptionId,
      date: props.valueDate,
      groupId: props.groupId,
      subject: props.subject,
    });
  }, [props.descriptionId, props.valueDate, props.groupId, props.subject]);

  const content =
    versionData.version === 'output' ? (
      <OutputSnapshotSubjectDetails
        {...props}
        inputDataDetailsType={data}
        versionData={versionData}
        canManage={hasPermissionToManageInputData}
        isLoading={isFetching}
        onReload={refetch}
        onAddComment={onUpdateComment}
      />
    ) : (
      <InputDataSubjectDetails
        {...props}
        inputDataDetailsType={data}
        versionData={versionData}
        canManage={hasPermissionToManageInputData}
        isLoading={isFetching}
        onReload={refetch}
        onAddComment={onUpdateComment}
      />
    );

  return (
    <>
      {mode === 'edit' ? (
        <Forbidden isAllowed={hasPermissionToManageInputData}>
          {content}
        </Forbidden>
      ) : (
        content
      )}
      {showOutputs && (
        <OutputVersionSelectionModal
          descriptionId={props.descriptionId}
          onClose={hideOutputSelect}
          onSelect={selectOutput}
          subject={props.subject}
          valueDate={props.valueDate}
        />
      )}
    </>
  );
}

function InputDataSubjectDetails(props: SubjectDetails) {
  const {
    inputDataDetailsType,
    versionData,
    isLoading: isDetailsLoading,
  } = props;
  const descriptionData = useInputDataDescriptions(
    inputDataDetailsType?.descriptions,
    versionData.version,
    props.descriptionId,
  );
  const description = descriptionData.description;
  const selectedVersion = useSubmittedVersion(description, versionData.version);

  const { data: info, isLoading: isInfoLoading } =
    useDescriptionInputDataInfoQuery(
      {
        descriptionId: description?.id ?? 0,
        valueDate: props.valueDate,
        subject: props.subject,
        dataStatus: versionData.version as 'active' | 'draft',
        submittedDataVersionId: selectedVersion?.id ?? undefined,
        sampleId: inputDataDetailsType?.sampleId ?? undefined,
      },
      { enabled: !!description?.id, throwOnError: true },
    );

  if ((isDetailsLoading && !inputDataDetailsType) || isInfoLoading) {
    return <Spinner overlay />;
  }

  if (!description?.id) {
    return <NotFound />;
  }

  if (!info) {
    return null;
  }

  // Keep the content mounted while reloading, so that the edit session and the lock are not released
  return (
    <>
      {isDetailsLoading && <Spinner overlay />}
      <InputDataSubjectDetailsInner
        {...props}
        info={info}
        descriptionData={descriptionData}
      />
    </>
  );
}

interface InputDataSubjectDetailsInnerProps extends SubjectDetails {
  info: InputDataInfoType;
  descriptionData: InputDataDescriptionInfo;
}

function InputDataSubjectDetailsInner(
  props: InputDataSubjectDetailsInnerProps,
) {
  const { t } = useTranslation();
  const {
    inputDataDetailsType,
    versionData,
    onReload,
    info,
    descriptionData,
    groupId,
    subject,
    valueDate,
    initialFilters,
    initialSort,
    initialPagination,
  } = props;
  const description = descriptionData.description;
  const selectedVersion = useSubmittedVersion(description, versionData.version);
  const [mode, setMode] = useInputDataDetailsStoreValue('mode');
  const [, setTotalElements] = useInputDataDetailsStoreValue('totalElements');
  const isEditMode = mode === 'edit';
  const { setError } = useAlertContext();

  const {
    filterParams,
    page,
    paginationProps,
    setQueryFilters,
    setTotal,
    size,
    sort,
    tableProps,
  } = useInputDataDirectory({
    columns: info.columns,
    initialFilters,
    initialSort,
    initialPagination,
    stateStorageKey: directoryStateStorageKey,
  });

  useEffect(() => {
    saveViewState('inputDataSubjectDetails', {
      valueDate: valueDate,
      descriptionId: description?.id ?? 0,
    });
  }, [description?.id, valueDate]);

  const scopeKey: string = useMemo(
    () =>
      InputDataScopeKey.create({
        descriptionId: description?.id?.toString() ?? '0',
        groupId,
        valueDate: valueDate ?? '',
        subject: subject ?? '',
        dataStatus: versionData.version === 'draft' ? 'draft' : 'active',
        versionId: selectedVersion?.id ?? undefined,
      }),
    [
      description?.id,
      groupId,
      valueDate,
      subject,
      versionData.version,
      selectedVersion?.id,
    ],
  );

  const sessionQuery = useInputDataEditSessionQuery(
    { scopeKey },
    { enabled: isEditMode },
  );

  useEffect(() => {
    if (isEditMode && sessionQuery.isError) {
      setError(t('editSession.openError'));
      setMode('view');
      sessionQuery.remove();
    }
  }, [isEditMode, sessionQuery, setError, setMode, t]);

  // Editing is blocked until the edit session has been reopened after a commit
  const [committedAt, setCommittedAt] = useState<number>();
  const isReopeningSession =
    committedAt != null &&
    sessionQuery.dataUpdatedAt <= committedAt &&
    !sessionQuery.isError;
  const handleSessionClose = useCallback(
    (reopen: boolean) => {
      if (reopen) {
        // Stay in edit mode: open a new edit session, rows reload with the new session id
        setCommittedAt(Date.now());
        void sessionQuery.invalidate();
        void onReload();
        return;
      }

      flushSync(() => {
        setQueryFilters?.({ pageNumber: 1 });
        setMode('view');
      });
      sessionQuery.remove();
    },
    [onReload, sessionQuery, setMode, setQueryFilters],
  );

  const descriptionInputDataQuery = useDescriptionInputDataQuery(
    {
      valueDate: props.valueDate,
      descriptionId: description?.id ?? 0,
      subject: props.subject,
      dataStatus: versionData.version as 'active' | 'draft',
      submittedDataVersionId: selectedVersion?.id ?? undefined,
      sampleId: inputDataDetailsType?.sampleId ?? undefined,
      page,
      size,
      sort,
      filterParams,
      sessionId: isEditMode ? sessionQuery.data?.id : undefined,
    },
    {
      enabled: isEditMode ? !!sessionQuery.data?.id : true,
      placeholderData: keepPreviousData,
    },
  );
  const {
    error,
    isLoading: isInputDataLoading,
    isFetching: isInputDataFetching,
    refetch,
  } = descriptionInputDataQuery;

  const { detailsData, subjectNameMappings } = useInputDataPage({
    info,
    page: descriptionInputDataQuery.data,
    setPaginationTotal: setTotal,
    setRowsTotal: setTotalElements,
  });

  const handleReload = useCallback(async () => {
    await Promise.all([onReload(), refetch()]);
  }, [onReload, refetch]);

  if (isInputDataLoading) {
    return <Spinner overlay delay={200} message={t('loading')} />;
  }

  const forbiddenErrorCode = getForbiddenErrorCode(error);
  if (forbiddenErrorCode) {
    return <Forbidden variant={forbiddenErrorCode} />;
  }

  return (
    <InputDataEditSessionProvider
      sessionId={sessionQuery.data?.id}
      onSessionClose={handleSessionClose}
      refreshRows={descriptionInputDataQuery.invalidate}
    >
      <DataSubjectDetailPage
        {...props}
        detailsData={detailsData}
        subjectNameMappings={subjectNameMappings}
        error={error}
        isLoading={props.isLoading || isInputDataFetching || isReopeningSession}
        onReload={handleReload}
        paginationProps={paginationProps}
        tableProps={tableProps}
      />
    </InputDataEditSessionProvider>
  );
}

function InputDataEditSessionProvider({
  sessionId,
  onSessionClose,
  refreshRows,
  children,
}: PropsWithChildren<
  Pick<
    EditSessionContextProviderProps,
    'sessionId' | 'onSessionClose' | 'refreshRows'
  >
>) {
  const updateRowsMutation = useUpdateInputDataDraftsMutation();
  const insertRowMutation = useInsertInputDataDraftMutation();
  const insertExcelRowMutation = useMutateSubjectInputDataUpload();
  const deleteRowsMutation = useDeleteInputDataDraftsMutation();
  const commitMutation = useCommitInputDataDraftsMutation();
  const discardMutation = useDiscardInputDataEditSessionMutation();

  return (
    <EditSessionContextProvider
      sessionId={sessionId}
      updateRowsFn={updateRowsMutation.mutateAsync}
      insertRowsFn={insertRowMutation.mutateAsync}
      insertExcelRowsFn={insertExcelRowMutation.mutateAsync}
      deleteRowsFn={deleteRowsMutation.mutateAsync}
      commitFn={commitMutation.mutateAsync}
      discardFn={discardMutation.mutateAsync}
      validateRowFn={acceptAll}
      onSessionClose={onSessionClose}
      reloadAfterInsert={false}
      refreshRows={refreshRows}
    >
      {children}
    </EditSessionContextProvider>
  );
}

function OutputSnapshotSubjectDetails(
  props: SubjectDetails & { output?: OutputSnapshotVersionType },
) {
  const [output, setOutput] = useState<OutputSnapshotVersionType | undefined>(
    props.output,
  );
  const defaultSelectedSubmittedDataId =
    props.output?.outputVersion.inputSources[0]?.submittedData.id;
  const [selectedSubmittedDataId, setSelectedSubmittedDataId] = useState<
    number | undefined
  >(defaultSelectedSubmittedDataId);
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const { data: outputSnapshot, error: outputSnapshotError } =
    useDescriptionOutputSnapshotVersionQuery(
      {
        descriptionId: props.descriptionId ?? 0,
        subject: props.subject,
        valueDate: props.valueDate,
        outputDataObjectVersionId: props.outputDataObjectVersionId ?? 0,
      },
      {
        enabled: !output && !!props.outputDataObjectVersionId,
      },
    );

  const { data: info, isLoading: isInfoLoading } =
    useDescriptionInputDataInfoQuery(
      {
        descriptionId: Number(props.descriptionId ?? 0),
        valueDate: props.valueDate,
        subject: props.subject,
        submittedDataVersionId: selectedSubmittedDataId,
      },
      {
        enabled: !!props.descriptionId && !!selectedSubmittedDataId,
        placeholderData: keepPreviousData,
        throwOnError: true,
      },
    );

  const resetPagination = useCallback(() => {
    setSearchParams((params) => {
      const newParams = new URLSearchParams(params);
      newParams.delete('pageNumber');
      return newParams;
    });
  }, [setSearchParams]);

  const selectSubmittedData = useCallback(
    (submittedDataId: number) => {
      if (selectedSubmittedDataId !== submittedDataId) {
        resetPagination();
      }
      setSelectedSubmittedDataId(submittedDataId);
    },
    [resetPagination, selectedSubmittedDataId],
  );

  useEffect(() => {
    const resolvedOutput = outputSnapshot ?? props.output;
    const submittedDataId =
      resolvedOutput?.outputVersion.inputSources[0]?.submittedData.id;
    setOutput(resolvedOutput);
    setSelectedSubmittedDataId(submittedDataId);
  }, [outputSnapshot, props.output]);

  if (props.isLoading || isInfoLoading) {
    return <Spinner overlay delay={200} message={t('loading')} />;
  }

  if (!info || !selectedSubmittedDataId) {
    return (
      <DataSubjectDetailPage
        {...props}
        error={outputSnapshotError}
        output={output}
        selectedSubmittedDataId={selectedSubmittedDataId}
        onSelectSubmittedDataId={selectSubmittedData}
      />
    );
  }

  return (
    <OutputSnapshotSubjectDetailsInner
      {...props}
      info={info}
      output={output}
      outputSnapshotError={outputSnapshotError}
      selectedSubmittedDataId={selectedSubmittedDataId}
      onSelectSubmittedDataId={selectSubmittedData}
    />
  );
}

interface OutputSnapshotSubjectDetailsInnerProps extends SubjectDetails {
  info: InputDataInfoType;
  output?: OutputSnapshotVersionType;
  outputSnapshotError?: ApiErrorResponse | null;
  selectedSubmittedDataId: number;
  onSelectSubmittedDataId: (submittedDataId: number) => void;
}

function OutputSnapshotSubjectDetailsInner(
  props: OutputSnapshotSubjectDetailsInnerProps,
) {
  const { t } = useTranslation();
  const { onReload } = props;
  const {
    filterParams,
    page,
    paginationProps,
    setTotal,
    size,
    sort,
    tableProps,
  } = useInputDataDirectory({
    columns: props.info.columns,
    initialFilters: props.initialFilters,
    initialSort: props.initialSort,
    initialPagination: props.initialPagination,
    stateStorageKey: directoryStateStorageKey,
  });

  const snapshotQuery = useDescriptionOutputSnapshotQuery(
    {
      descriptionId: props.descriptionId!,
      submittedDataId: props.selectedSubmittedDataId,
      valueDate: props.valueDate,
      page,
      size,
      sort,
      filterParams,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const { error, isLoading, isFetching, refetch } = snapshotQuery;

  const { detailsData, subjectNameMappings } = useInputDataPage({
    info: props.info,
    page: snapshotQuery.data,
    setPaginationTotal: setTotal,
  });

  const handleReload = useCallback(async () => {
    await Promise.all([onReload(), refetch()]);
  }, [onReload, refetch]);

  if (isLoading) {
    return <Spinner overlay delay={200} message={t('loading')} />;
  }

  return (
    <DataSubjectDetailPage
      {...props}
      detailsData={detailsData}
      subjectNameMappings={subjectNameMappings}
      error={error ?? props.outputSnapshotError}
      isLoading={props.isLoading || isFetching}
      onReload={handleReload}
      paginationProps={paginationProps}
      tableProps={tableProps}
    />
  );
}

interface DataSubjectDetailPageProps extends SubjectDetails {
  detailsData?: InputDataType;
  subjectNameMappings?: Record<string, string>;
  error?: ApiErrorResponse | null;
  output?: OutputSnapshotVersionType;
  selectedSubmittedDataId?: number;
  onSelectSubmittedDataId?: (submittedDataId: number) => void;
  canManage: boolean;
  onReload: () => Promise<any>;
  onAddComment: (comment: SampleCommentType) => void;
  paginationProps?: PaginationProps;
  tableProps?: TableFilterSortProps;
}

function isDataLoaded(
  sessionId: string | undefined,
  detailsData: InputDataType | undefined,
) {
  return (
    !!sessionId &&
    (detailsData?.rows?.length === 0 || !!detailsData?.rows?.[0].id)
  );
}

function DataSubjectDetailPage(props: DataSubjectDetailPageProps) {
  const [mode] = useInputDataDetailsStoreValue('mode');

  return mode === 'edit' ? (
    <DataSubjectDetailEditPage {...props} />
  ) : (
    <DataSubjectDetailViewPage {...props} />
  );
}

function DataSubjectDetailEditPage(props: DataSubjectDetailPageProps) {
  const {
    inputDataDetailsType,
    detailsData,
    subjectNameMappings,
    error,
    versionData,
    paginationProps,
  } = props;
  const { sessionId } = useEditSessionContext();
  const { t } = useTranslation();
  const descriptionData = useInputDataDescriptions(
    inputDataDetailsType?.descriptions,
    versionData.version,
    props.descriptionId,
  );
  const description = descriptionData.description;
  const selectedVersion = useSubmittedVersion(description, versionData.version);

  if (!isDataLoaded(sessionId, detailsData)) {
    return <Spinner overlay delay={200} message={t('loading')} />;
  }

  return (
    <InputDataSubjectDetailsForm
      {...props}
      inputDataDetailsType={inputDataDetailsType}
      descriptionData={descriptionData}
      detailsData={detailsData}
      subjectNameMappings={subjectNameMappings}
      detailsError={error}
      selectedVersion={selectedVersion}
      versionData={versionData}
      paginationProps={paginationProps}
    />
  );
}

function DataSubjectDetailViewPage(props: DataSubjectDetailPageProps) {
  const {
    inputDataDetailsType,
    detailsData,
    subjectNameMappings,
    error,
    output,
    versionData,
  } = props;
  const descriptionData = useInputDataDescriptions(
    inputDataDetailsType?.descriptions,
    versionData.version,
    props.descriptionId,
  );
  const description = descriptionData.description;
  const selectedVersion = useSubmittedVersion(description, versionData.version);

  return (
    <InputDataSubjectDetailView
      {...props}
      descriptionData={descriptionData}
      detailsData={detailsData}
      subjectNameMappings={subjectNameMappings}
      detailsError={error}
      output={output}
      selectedVersion={selectedVersion}
      versionData={versionData}
    />
  );
}
